import './App.css';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	Link,
	useLocation,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { RxDashboard } from 'react-icons/rx';
import { RiAdminLine } from 'react-icons/ri';
import { IoArrowBackOutline } from 'react-icons/io5';
import {
	FaUsersCog,
	FaUser,
	FaCheckSquare,
	FaCog,
	FaChartLine,
	FaClock,
	FaBook,
	FaKey,
	FaCode,
	FaBuilding,
	FaBullseye,
} from 'react-icons/fa';
import PropTypes from 'prop-types';

import { AuthProvider, useAdminAccess, useAuth, useUserRole } from './contexts/auth_context';
import { ThemeProvider, useTheme } from './contexts/theme_context';
import { ErrorBoundaryWrapper } from './components/errors/error_boundary';
import { SideBar } from './views/sidebar_view';
import { WelcomeSection } from './components/welcome_section';
import { UserAdmin } from './views/users_admin_list_view';
import { UserAdminUserSettings } from './views/user_admin_edit_view';
import { getOrganizations } from './services/organization_service';
import { logSystemEvent } from './utils/logs';
import LogsView from './views/logs_view';
import TodoView from './views/todo_view';
import GoogleCalendarCallbackView from './views/google_calendar_callback_view';

const AuthController = lazy(() => import('./controllers/auth_controller'));
const DashboardController = lazy(() => import('./controllers/dashboard_controller'));
const AdminController = lazy(() => import('./controllers/admin_controller'));
const ResetPasswordController = lazy(() => import('./controllers/reset_password_controller'));
const ForgotPasswordController = lazy(() => import('./controllers/forgot_password_controller'));
const AccountActivationController = lazy(
	() => import('./controllers/account_activation_controller')
);
const InviteLinkController = lazy(() => import('./controllers/invite_link_controller'));
const AdminDashboardView = lazy(() => import('./views/admin_dashboard_view'));
const AdminLookerSettingsView = lazy(() => import('./views/admin_settings_view'));
const AdminIntegrationsView = lazy(() => import('./views/admin_integrations_view'));
const AdminOrganizationsView = lazy(() => import('./views/admin_organizations_view'));
const TrackingAppView = lazy(() => import('./views/tracking_app_view'));
const TimeTrackingView = lazy(() => import('./views/time_tracking_view'));
const CodeLibraryView = lazy(() => import('./views/code_library_view'));
const WikiView = lazy(() => import('./views/wiki_view'));
const WikiPageView = lazy(() => import('./views/wiki_page_view'));
const LookerWrapperView = lazy(() => import('./views/looker_wrapper_view'));
const LookerDashboardView = lazy(() => import('./views/looker_dashboard_view'));

const TOTPVerificationView = lazy(() => import('./views/totp_verification_view'));
const BackupCodeVerificationView = lazy(() => import('./views/backup_code_verification_view'));
const SetupTOTPView = lazy(() => import('./views/setup_totp_view'));
const ProfileController = lazy(() => import('./controllers/profile_controller'));
const ClientDashboardView = lazy(() => import('./views/client_dashboard_view'));
const ClientPerformanceView = lazy(() => import('./views/client_performance_view'));
const ClientRelationshipView = lazy(() => import('./views/client_relationship_view'));
const ProspectsView = lazy(() => import('./views/prospects_view'));

const LoadingSpinner = () => (
	<div className='flex items-center justify-center min-h-screen'>
		<div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500'></div>
	</div>
);

const DEFAULT_VALIDATION_ERROR_GRACE_MS = 2500;
const LOOKER_VALIDATION_ERROR_GRACE_MS = 8000;
const PUBLIC_LIGHT_ONLY_PATHS = [
	'/',
	'/login',
	'/forgot-password',
	'/reset-password',
	'/activate-account',
	'/invite-complete',
	'/verify-totp',
	'/verify-backup-code',
	'/setup-totp',
];

const normalizePathname = pathname => {
	if (!pathname) return '/';
	if (pathname === '/') return '/';
	return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

const isRunningInIframe = () => {
	return globalThis.self !== globalThis.top;
};

const IframeAuthFallback = ({ targetPath, message }) => {
	useEffect(() => {
		if (!isRunningInIframe()) {
			return;
		}

		globalThis.parent.postMessage(
			{
				type: 'LOOKER_AUTH_REQUIRED',
				targetPath,
			},
			globalThis.location.origin
		);
	}, [targetPath]);

	return (
		<div className='w-full h-screen bg-gray-50 flex items-center justify-center p-4'>
			<div className='max-w-md w-full rounded-xl border border-amber-200 bg-white p-6 text-center'>
				<h1 className='text-lg font-semibold text-amber-900 mb-2'>
					Session Verification Needed
				</h1>
				<p className='text-sm text-gray-700'>{message}</p>
			</div>
		</div>
	);
};

const useValidationErrorGrace = ({
	isAuthenticated,
	backendValidated,
	backendAuthStage,
	graceMs = DEFAULT_VALIDATION_ERROR_GRACE_MS,
}) => {
	const [isInValidationErrorGrace, setIsInValidationErrorGrace] = useState(false);
	const graceTimeoutRef = useRef(null);

	useEffect(() => {
		const isValidationErrorState =
			isAuthenticated && !backendValidated && backendAuthStage === 'validation_error';

		if (graceTimeoutRef.current) {
			clearTimeout(graceTimeoutRef.current);
			graceTimeoutRef.current = null;
		}

		if (!isValidationErrorState) {
			setIsInValidationErrorGrace(false);
			return undefined;
		}

		setIsInValidationErrorGrace(true);
		graceTimeoutRef.current = setTimeout(() => {
			setIsInValidationErrorGrace(false);
			graceTimeoutRef.current = null;
		}, graceMs);

		return () => {
			if (graceTimeoutRef.current) {
				clearTimeout(graceTimeoutRef.current);
				graceTimeoutRef.current = null;
			}
		};
	}, [isAuthenticated, backendValidated, backendAuthStage, graceMs]);

	return isInValidationErrorGrace;
};

const isLookerValidationErrorState = ({ isAuthenticated, backendValidated, backendAuthStage }) =>
	isAuthenticated && !backendValidated && backendAuthStage === 'validation_error';

const getLookerValidationStateSignature = ({
	isAuthenticated,
	backendValidated,
	backendAuthStage,
	isInValidationErrorGrace,
}) =>
	[
		isAuthenticated ? 'auth' : 'anon',
		backendValidated ? 'validated' : 'not_validated',
		backendAuthStage ?? 'nullish_stage',
		isInValidationErrorGrace ? 'grace_on' : 'grace_off',
	].join('|');

const emitLookerValidationGraceStateEvent = ({
	pathname,
	isInValidationErrorGrace,
	validationErrorGraceMs,
	backendAuthStage,
}) => {
	logSystemEvent('looker_validation_grace_state', 'warning', {
		path: pathname,
		graceActive: isInValidationErrorGrace,
		graceMs: validationErrorGraceMs,
		authenticated: true,
		backendValidated: false,
		backendAuthStage,
	});
};

const emitLookerValidationRecoveredEvent = ({
	pathname,
	validationErrorGraceMs,
	backendAuthStage,
}) => {
	logSystemEvent('looker_validation_recovered', 'info', {
		path: pathname,
		graceMs: validationErrorGraceMs,
		backendAuthStage,
	});
};

const useLookerValidationGraceTelemetry = ({
	pathname,
	isAuthenticated,
	backendValidated,
	backendAuthStage,
	isInValidationErrorGrace,
	validationErrorGraceMs,
}) => {
	const lastTelemetryStateRef = useRef('');
	const previousGraceActiveRef = useRef(false);

	useEffect(() => {
		const isLookerRoute = pathname === '/looker';
		if (!isLookerRoute) {
			lastTelemetryStateRef.current = '';
			previousGraceActiveRef.current = false;
			return;
		}

		const isValidationErrorState = isLookerValidationErrorState({
			isAuthenticated,
			backendValidated,
			backendAuthStage,
		});

		const didGraceExpireWithoutRecovery =
			previousGraceActiveRef.current && !isInValidationErrorGrace && isValidationErrorState;

		if (didGraceExpireWithoutRecovery) {
			logSystemEvent('looker_validation_grace_expired', 'warning', {
				path: pathname,
				graceMs: validationErrorGraceMs,
				authenticated: true,
				backendValidated: false,
				backendAuthStage,
				reason: 'no_recovery_within_grace_window',
			});
		}

		const stateSignature = getLookerValidationStateSignature({
			isAuthenticated,
			backendValidated,
			backendAuthStage,
			isInValidationErrorGrace,
		});

		previousGraceActiveRef.current = isInValidationErrorGrace;

		if (lastTelemetryStateRef.current === stateSignature) {
			return;
		}

		lastTelemetryStateRef.current = stateSignature;

		if (isValidationErrorState) {
			emitLookerValidationGraceStateEvent({
				pathname,
				isInValidationErrorGrace,
				validationErrorGraceMs,
				backendAuthStage,
			});
			return;
		}

		if (isAuthenticated && backendValidated) {
			emitLookerValidationRecoveredEvent({
				pathname,
				validationErrorGraceMs,
				backendAuthStage,
			});
		}
	}, [
		pathname,
		isAuthenticated,
		backendValidated,
		backendAuthStage,
		isInValidationErrorGrace,
		validationErrorGraceMs,
	]);
};

const useRouteRedirectTelemetry = (eventName, reason, enabled = true) => {
	const lastReasonRef = useRef('');

	useEffect(() => {
		if (!enabled || !reason) {
			return;
		}

		if (lastReasonRef.current === reason) {
			return;
		}

		lastReasonRef.current = reason;
		logSystemEvent(eventName, 'warning', {
			reason,
			path: globalThis?.location?.pathname || null,
		});
	}, [enabled, eventName, reason]);
};

const getValidatedRouteRedirectReason = ({
	isLoadingAuth,
	isAuthRecoveryPending,
	isAuthenticated,
	isBackendValidationPending,
	backendValidated,
	backendAuthStage,
}) => {
	if (!isLoadingAuth && !isAuthRecoveryPending && !isAuthenticated) {
		return 'unauthenticated';
	}

	if (!isLoadingAuth && !isBackendValidationPending && !backendValidated) {
		if (backendAuthStage === 'requires_totp') {
			return 'requires_totp';
		}
		if (backendAuthStage === 'requires_totp_setup') {
			return 'requires_totp_setup';
		}
		return 'validation_failed_redirect_home';
	}

	return null;
};

const getProtectedAdminRouteRedirectReason = ({
	isLoadingAuth,
	isAuthRecoveryPending,
	isAuthenticated,
	isBackendValidationPending,
	backendValidated,
	backendAuthStage,
	isLoadingAccess,
	hasAdminAccess,
}) => {
	const validatedRouteReason = getValidatedRouteRedirectReason({
		isLoadingAuth,
		isAuthRecoveryPending,
		isAuthenticated,
		isBackendValidationPending,
		backendValidated,
		backendAuthStage,
	});

	if (validatedRouteReason) {
		return validatedRouteReason;
	}

	if (!isLoadingAuth && !isLoadingAccess && backendValidated && !hasAdminAccess) {
		return 'missing_admin_access';
	}

	return null;
};

IframeAuthFallback.propTypes = {
	targetPath: PropTypes.string.isRequired,
	message: PropTypes.string.isRequired,
};

const ValidatedRoute = ({ children }) => {
	const location = useLocation();
	const {
		isAuthenticated,
		isLoadingAuth,
		isAuthRecoveryPending,
		backendValidated,
		backendAuthStage,
	} = useAuth();
	const validationErrorGraceMs =
		location.pathname === '/looker'
			? LOOKER_VALIDATION_ERROR_GRACE_MS
			: DEFAULT_VALIDATION_ERROR_GRACE_MS;
	const isInValidationErrorGrace = useValidationErrorGrace({
		isAuthenticated,
		backendValidated,
		backendAuthStage,
		graceMs: validationErrorGraceMs,
	});
	const isBackendValidationPending =
		!backendValidated &&
		(isInValidationErrorGrace ||
			backendAuthStage === 'unknown' ||
			backendAuthStage === null ||
			backendAuthStage === undefined);

	useLookerValidationGraceTelemetry({
		pathname: location.pathname,
		isAuthenticated,
		backendValidated,
		backendAuthStage,
		isInValidationErrorGrace,
		validationErrorGraceMs,
	});

	const redirectReason = getValidatedRouteRedirectReason({
		isLoadingAuth,
		isAuthRecoveryPending,
		isAuthenticated,
		isBackendValidationPending,
		backendValidated,
		backendAuthStage,
	});

	useRouteRedirectTelemetry('validated_route_redirect', redirectReason, Boolean(redirectReason));

	if (isLoadingAuth || isAuthRecoveryPending) {
		return <LoadingSpinner />;
	}

	if (!isAuthenticated) {
		return <Navigate to='/' replace />;
	}

	if (isBackendValidationPending) {
		return <LoadingSpinner />;
	}

	if (!backendValidated) {
		if (backendAuthStage === 'requires_totp') {
			return <Navigate to='/verify-totp' replace />;
		}

		if (backendAuthStage === 'requires_totp_setup') {
			return <Navigate to='/setup-totp' replace />;
		}

		return <Navigate to='/' replace />;
	}

	return children;
};

ValidatedRoute.propTypes = {
	children: PropTypes.node.isRequired,
};

const ProtectedAdminRoute = ({ children }) => {
	const { hasAdminAccess, isLoadingAccess } = useAdminAccess();
	const {
		isAuthenticated,
		isLoadingAuth,
		isAuthRecoveryPending,
		backendValidated,
		backendAuthStage,
	} = useAuth();
	const isInValidationErrorGrace = useValidationErrorGrace({
		isAuthenticated,
		backendValidated,
		backendAuthStage,
	});
	const isBackendValidationPending =
		!backendValidated &&
		(isInValidationErrorGrace ||
			backendAuthStage === 'unknown' ||
			backendAuthStage === null ||
			backendAuthStage === undefined);

	const redirectReason = getProtectedAdminRouteRedirectReason({
		isLoadingAuth,
		isAuthRecoveryPending,
		isAuthenticated,
		isBackendValidationPending,
		backendValidated,
		backendAuthStage,
		isLoadingAccess,
		hasAdminAccess,
	});

	useRouteRedirectTelemetry(
		'protected_admin_route_redirect',
		redirectReason,
		Boolean(redirectReason)
	);

	if (isLoadingAuth || isAuthRecoveryPending || isLoadingAccess) {
		return <LoadingSpinner />;
	}

	if (!isAuthenticated) {
		return <Navigate to='/' replace />;
	}

	if (isBackendValidationPending) {
		return <LoadingSpinner />;
	}

	if (!backendValidated) {
		if (backendAuthStage === 'requires_totp') {
			return <Navigate to='/verify-totp' replace />;
		}

		if (backendAuthStage === 'requires_totp_setup') {
			return <Navigate to='/setup-totp' replace />;
		}

		return <Navigate to='/' replace />;
	}

	if (!hasAdminAccess) {
		return <Navigate to='/' replace />;
	}

	return children;
};

ProtectedAdminRoute.propTypes = {
	children: PropTypes.node.isRequired,
};

const LookerWrapperRoute = () => {
	const {
		isAuthenticated,
		isLoadingAuth,
		isAuthRecoveryPending,
		backendValidated,
		backendAuthStage,
	} = useAuth();

	if (isLoadingAuth || isAuthRecoveryPending) {
		return <LoadingSpinner />;
	}

	if (!isAuthenticated) {
		if (isRunningInIframe()) {
			return (
				<IframeAuthFallback
					targetPath='/'
					message='Your session has ended. Please sign in again in the main app window.'
				/>
			);
		}
		return <Navigate to='/' replace />;
	}

	if (!backendValidated) {
		if (isRunningInIframe()) {
			const targetPath =
				backendAuthStage === 'requires_totp_setup' ? '/setup-totp' : '/verify-totp';
			return (
				<IframeAuthFallback
					targetPath={targetPath}
					message='TOTP verification is required. Continue in the main app window.'
				/>
			);
		}

		if (backendAuthStage === 'requires_totp_setup') {
			return <Navigate to='/setup-totp' replace />;
		}

		if (backendAuthStage === 'requires_totp') {
			return <Navigate to='/verify-totp' replace />;
		}

		return <Navigate to='/' replace />;
	}

	return <LookerWrapperView />;
};

const orgNameToSlug = name =>
	name
		.toLowerCase()
		.replace(/\s+/g, '-')
		.replace(/[^a-z0-9-]/g, '');

const ClientOrgRoute = ({ children }) => {
	const { backendValidated, backendUser } = useAuth();
	const { userRole, isLoadingRole } = useUserRole();
	const { orgDashboard } = useParams();

	if (!backendValidated || isLoadingRole) return <LoadingSpinner />;

	const userOrgSlug = backendUser?.organizationName
		? orgNameToSlug(backendUser.organizationName)
		: null;

	const allowedOrgPages = userOrgSlug
		? [`${userOrgSlug}-dashboard`, `${userOrgSlug}-performance`]
		: [];

	if (userRole !== 'client' || !allowedOrgPages.includes(orgDashboard)) {
		return <Navigate to='/' replace />;
	}

	return children;
};

ClientOrgRoute.propTypes = {
	children: PropTypes.node.isRequired,
};

const ClientOrgPage = () => {
	const { orgDashboard } = useParams();
	if (orgDashboard?.endsWith('-performance')) {
		return <ClientPerformanceView />;
	}
	return <ClientDashboardView />;
};

// Internal roles (superuser/admin/consultant) may view any client's dashboard;
// the client role is redirected away.
const InternalRoute = ({ children }) => {
	const { backendValidated } = useAuth();
	const { userRole, isLoadingRole } = useUserRole();

	if (!backendValidated || isLoadingRole) return <LoadingSpinner />;
	if (!userRole || userRole === 'client') {
		return <Navigate to='/' replace />;
	}
	return children;
};

InternalRoute.propTypes = {
	children: PropTypes.node.isRequired,
};

const InternalClientPage = () => {
	const { orgId } = useParams();
	const location = useLocation();
	const isPerformance = location.pathname.endsWith('/performance');
	const isRelationship = location.pathname.endsWith('/relationship');

	// Underline tabs: the active one is marked by weight of colour and a rule beneath
	// it, so the bar stays quiet chrome above the page it labels
	const tabStyle = active => ({
		padding: '0 4px 9px',
		fontSize: 13,
		fontWeight: 400,
		textDecoration: 'none',
		color: active ? 'var(--app-text)' : 'var(--app-text-muted)',
		background: 'transparent',
		borderBottom: active ? '2px solid var(--sb-active)' : '2px solid transparent',
	});

	return (
		<div>
			{/* The gutter matches the views below so the tabs share their left edge */}
			<div
				className='px-6 md:px-8 pt-3'
				style={{
					position: 'sticky',
					top: 96,
					zIndex: 20,
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					background: 'var(--app-bg)',
					borderBottom: '0.5px solid var(--app-border)',
					marginBottom: 18,
				}}
			>
				<Link to={`/clients/${orgId}`} style={tabStyle(!isPerformance && !isRelationship)}>
					Dashboard
				</Link>
				<Link to={`/clients/${orgId}/performance`} style={tabStyle(isPerformance)}>
					Performance
				</Link>
				<Link to={`/clients/${orgId}/relationship`} style={tabStyle(isRelationship)}>
					Relationship
				</Link>
				{/* Only the Relationship tab is badged. The whole page is internal, but
				    the badge is there to warn that this tab holds commercial terms and
				    candid account notes, which the Dashboard and Performance tabs do not.
				    Literal amber because the theme has no warning token, and an opaque
				    pill reads the same in both themes. */}
				{isRelationship && (
					<span
						style={{
							marginLeft: 'auto',
							alignSelf: 'center',
							fontSize: 11,
							padding: '3px 9px',
							borderRadius: 8,
							background: '#FBEBC8',
							color: '#7A4708',
						}}
					>
						Internal only
					</span>
				)}
			</div>
			{isRelationship && <ClientRelationshipView organizationId={orgId} />}
			{isPerformance && <ClientPerformanceView organizationId={orgId} />}
			{!isPerformance && !isRelationship && <ClientDashboardView organizationId={orgId} />}
		</div>
	);
};

const AppContent = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { hasAdminAccess, isLoadingAccess } = useAdminAccess();
	const { backendValidated, backendUser } = useAuth();
	const { userRole, isLoadingRole } = useUserRole();
	const { theme } = useTheme();
	const pathname = normalizePathname(location.pathname);
	const isAdminPage = pathname.startsWith('/admin');
	const isPublicLightOnlyRoute = PUBLIC_LIGHT_ONLY_PATHS.includes(pathname);
	const isClient = !isLoadingRole && userRole === 'client';

	const clientOrgSlug =
		isClient && backendUser?.organizationName
			? orgNameToSlug(backendUser.organizationName)
			: null;
	const clientDashboardPath = clientOrgSlug ? `/${clientOrgSlug}-dashboard` : null;
	const clientPerformancePath = clientOrgSlug ? `/${clientOrgSlug}-performance` : null;

	const isClientPathAllowed =
		pathname === '/profile' ||
		(clientDashboardPath && pathname === clientDashboardPath) ||
		(clientPerformancePath && pathname === clientPerformancePath);

	// Internal roles (non-client) can browse every client's dashboard
	const isInternalUser = !isLoadingRole && !!userRole && userRole !== 'client';
	const [clients, setClients] = useState([]);

	useEffect(() => {
		if (!backendValidated || !isInternalUser) {
			return undefined;
		}
		let cancelled = false;
		getOrganizations()
			.then(orgs => {
				if (!cancelled) setClients(Array.isArray(orgs) ? orgs : []);
			})
			.catch(() => {
				if (!cancelled) setClients([]);
			});
		return () => {
			cancelled = true;
		};
	}, [backendValidated, isInternalUser]);

	useEffect(() => {
		if (isClient && !isClientPathAllowed) {
			navigate(clientDashboardPath || '/profile', { replace: true });
		}
	}, [isClient, isClientPathAllowed, navigate, clientDashboardPath]);

	useEffect(() => {
		if (typeof document === 'undefined') {
			return;
		}

		const effectiveTheme = isPublicLightOnlyRoute ? 'light' : theme;
		document.documentElement.setAttribute('data-theme', effectiveTheme);
		document.body.setAttribute('data-theme', effectiveTheme);
	}, [isPublicLightOnlyRoute, theme]);

	const adminMenuItems = useMemo(
		() => [
			{
				key: 'dashboard',
				label: 'Dashboard',
				icon: <RxDashboard />,
				path: '/admin/dashboard',
			},
			{ key: 'users', label: 'Users', icon: <FaUsersCog />, path: '/admin/users' },
			{
				key: 'organizations',
				label: 'Organizations',
				icon: <FaBuilding />,
				path: '/admin/organizations',
			},
			{ key: 'looker', label: 'Looker', icon: <FaCog />, path: '/admin/looker' },
			{
				key: 'integrations',
				label: 'Integrations',
				icon: <FaKey />,
				path: '/admin/integrations',
			},
			{ key: 'logs', label: 'Logs', icon: '📜', path: '/admin/logs' },
			{
				key: 'general',
				label: 'Back to App',
				icon: <IoArrowBackOutline />,
				path: '/dashboard',
			},
		],
		[]
	);

	const generalMenuItems = useMemo(() => {
		if (isLoadingRole) {
			return [];
		}
		if (isClient) {
			const clientItems = [
				{ key: 'profile', label: 'Profile', icon: <FaUser />, path: '/profile' },
			];
			if (clientDashboardPath) {
				clientItems.unshift(
					{
						key: 'dashboard',
						label: 'Dashboard',
						icon: <RxDashboard />,
						path: clientDashboardPath,
					},
					{
						key: 'performance',
						label: 'Performance',
						icon: <FaChartLine />,
						path: clientPerformancePath,
					}
				);
			}
			return clientItems;
		}

		const items = [
			{ key: 'dashboard', label: 'Dashboard', icon: <RxDashboard />, path: '/dashboard' },
			{ key: 'todos', label: 'Todo List', icon: <FaCheckSquare />, path: '/todos' },
			{
				key: 'wiki',
				label: 'Knowledge Base',
				icon: <FaBook />,
				path: '/wiki',
			},
			{
				key: 'code-library',
				label: 'Library',
				icon: <FaCode />,
				path: '/code-library',
			},
			{
				key: 'tracking-app',
				label: 'Events',
				icon: <FaChartLine />,
				path: '/tracking-app',
			},
			{
				key: 'time-tracking',
				label: 'Time Tracking',
				icon: <FaClock />,
				path: '/time-tracking',
			},
			{
				key: 'looker',
				label: 'Looker',
				icon: <FaChartLine />,
				path: '/looker',
			},
			{
				key: 'prospects',
				label: 'Prospects',
				icon: <FaBullseye />,
				path: '/prospects',
				dividerBefore: true,
			},
			{
				key: 'clients',
				label: 'Clients',
				icon: <FaBuilding />,
				emptyLabel: 'No clients yet',
				children: clients.map(client => ({
					key: `client-${client.id}`,
					label: client.name,
					path: `/clients/${client.id}`,
				})),
			},
			{
				key: 'profile',
				label: 'Profile',
				icon: <FaUser />,
				path: '/profile',
				dividerBefore: true,
			},
		];

		if (hasAdminAccess === true && !isLoadingAccess) {
			items.push({
				key: 'admin',
				label: 'Admin',
				icon: <RiAdminLine />,
				path: '/admin/dashboard',
			});
		}

		return items;
	}, [
		hasAdminAccess,
		isLoadingAccess,
		isClient,
		isLoadingRole,
		clientDashboardPath,
		clientPerformancePath,
		clients,
	]);

	const menuItems = isAdminPage ? adminMenuItems : generalMenuItems;
	const title = isAdminPage ? 'Admin' : 'App';

	// When viewing a client, show that organisation's name in the header
	const viewedClientMatch = pathname.match(/^\/clients\/([^/]+)/);
	const viewedClient = viewedClientMatch
		? clients.find(client => String(client.id) === viewedClientMatch[1])
		: null;

	const showSidebar =
		backendValidated &&
		location.pathname !== '/' &&
		location.pathname !== '/forgot-password' &&
		location.pathname !== '/reset-password' &&
		location.pathname !== '/activate-account' &&
		location.pathname !== '/verify-totp' &&
		location.pathname !== '/verify-backup-code' &&
		location.pathname !== '/setup-totp' &&
		location.pathname !== '/looker-wrapper' &&
		location.pathname !== '/google-calendar/callback';

	return (
		<>
			{showSidebar && (
				<SideBar
					key={isAdminPage ? 'admin-sidebar' : 'general-sidebar'}
					title={title}
					menuItems={menuItems}
					defaultActiveKey='dashboard'
				/>
			)}
			<main className='content'>
				{showSidebar && !isClient && (
					<WelcomeSection titleOverride={viewedClient?.name || null} />
				)}
				<Suspense fallback={<LoadingSpinner />}>
					<Routes>
						<Route path='/' element={<AuthController />} />
						<Route path='/verify-totp' element={<TOTPVerificationView />} />
						<Route
							path='/verify-backup-code'
							element={<BackupCodeVerificationView />}
						/>
						<Route
							path='/setup-totp'
							element={
								<ValidatedRoute>
									<SetupTOTPView />
								</ValidatedRoute>
							}
						/>
						<Route path='/forgot-password' element={<ForgotPasswordController />} />
						<Route path='/reset-password' element={<ResetPasswordController />} />
						<Route path='/activate-account' element={<AccountActivationController />} />
						<Route path='/invite-complete' element={<InviteLinkController />} />
						<Route path='/looker-wrapper' element={<LookerWrapperRoute />} />
						<Route
							path='/google-calendar/callback'
							element={<GoogleCalendarCallbackView />}
						/>
						<Route
							path='/dashboard'
							element={
								<ValidatedRoute>
									<DashboardController />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/profile'
							element={
								<ValidatedRoute>
									<ProfileController />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/todos'
							element={
								<ValidatedRoute>
									<TodoView />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/code-library'
							element={
								<ValidatedRoute>
									<CodeLibraryView />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/wiki'
							element={
								<ValidatedRoute>
									<WikiView />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/wiki/:id'
							element={
								<ValidatedRoute>
									<WikiPageView />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/time-tracking'
							element={
								<ValidatedRoute>
									<TimeTrackingView />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/looker'
							element={
								<ValidatedRoute>
									<LookerDashboardView />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/admin'
							element={
								<ProtectedAdminRoute>
									<AdminController />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/admin/dashboard'
							element={
								<ProtectedAdminRoute>
									<AdminDashboardView />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/admin/permissions'
							element={
								<ProtectedAdminRoute>
									<UserAdminUserSettings />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/admin/logs'
							element={
								<ProtectedAdminRoute>
									<LogsView />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/admin/looker'
							element={
								<ProtectedAdminRoute>
									<AdminLookerSettingsView />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/admin/integrations'
							element={
								<ProtectedAdminRoute>
									<AdminIntegrationsView />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/tracking-app'
							element={
								<ValidatedRoute>
									<TrackingAppView />
								</ValidatedRoute>
							}
						/>
						<Route
							path='/admin/users'
							element={
								<ProtectedAdminRoute>
									<UserAdmin />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/admin/organizations'
							element={
								<ProtectedAdminRoute>
									<AdminOrganizationsView />
								</ProtectedAdminRoute>
							}
						/>
						<Route
							path='/prospects'
							element={
								<InternalRoute>
									<ProspectsView />
								</InternalRoute>
							}
						/>
						<Route
							path='/clients/:orgId'
							element={
								<InternalRoute>
									<InternalClientPage />
								</InternalRoute>
							}
						/>
						<Route
							path='/clients/:orgId/performance'
							element={
								<InternalRoute>
									<InternalClientPage />
								</InternalRoute>
							}
						/>
						<Route
							path='/clients/:orgId/relationship'
							element={
								<InternalRoute>
									<InternalClientPage />
								</InternalRoute>
							}
						/>
						<Route
							path='/:orgDashboard'
							element={
								<ClientOrgRoute>
									<ClientOrgPage />
								</ClientOrgRoute>
							}
						/>
						<Route path='*' element={<Navigate to='/' replace />} />
					</Routes>
				</Suspense>
			</main>
		</>
	);
};

export const App = () => {
	return (
		<AuthProvider>
			<ThemeProvider>
				<ErrorBoundaryWrapper>
					<div className='App'>
						<Router>
							<AppContent />
						</Router>
					</div>
				</ErrorBoundaryWrapper>
			</ThemeProvider>
		</AuthProvider>
	);
};
